import React from 'react';
import { type RouteComponentProps } from '@gatsbyjs/reach-router';
import { useRecoilState } from 'recoil';
import { biedwijzerContentState } from '../..';
import AppDetailsContainer from '../../../components/AppDetailsContainer';

import { StepName } from '../MainPreview/types';
import MainPreview from '../MainPreview';
import ToggleCheckbox from '~/components/molecule/ToggleCheckbox';
import type { AppStatus_Biedwijzer_Report_Cta } from '~/graphql/types';
import JustificationContainer from '~/components/atom/JustificationContainer';
import styled from 'styled-components';
import { Body } from '~/components/atom/Typography';
import Divider from '~/components/atom/Divider';
import CallToActionBlock from '~/components/organism/CallToActionBlock';
import useOffices from '~/hooks/useOffices';

const text = {
  header: 'Rapport',
  helpWithViewingLabel: `Toon button 'Plan een bezichtiging in'`,
  helpWithBiddingLabel: `Toon button 'Help mij met het uitbrengen van een bod'`,
  noWorkingAreaHeader: 'Geen werkgebieden ingesteld',
  noWorkingAreaDescription:
    'Voeg werkgebieden toe aan je vestigingen zodat de buttons worden weergegeven op de rapport pagina.',
  noWorkingAreaButtonSingle: 'Werkgebied instellen',
  noWorkingAreaButtonMultiple: 'Werkgebieden toevoegen',
};

type Props = RouteComponentProps;

const Report: React.FCC<Props> = () => {
  const [values, setValues] = useRecoilState(biedwijzerContentState);
  const offices = useOffices();

  const hasInclusiveWorkingAreas = offices.some(
    office => (office.workingAreas?.inclusive.length ?? 0) > 0,
  );
  const isSingleOffice = offices.length === 1;
  const ctaLink = isSingleOffice
    ? `/-/settings/offices/${offices[0].id}/working-area`
    : '/-/settings/offices';
  const ctaButtonLabel = isSingleOffice
    ? text.noWorkingAreaButtonSingle
    : text.noWorkingAreaButtonMultiple;

  const onUpdateCta = (
    fieldName: keyof AppStatus_Biedwijzer_Report_Cta,
    value: boolean,
  ) => {
    setValues(prev => {
      if (!prev) return prev;

      return {
        ...prev,
        content: prev.content
          ? {
              ...prev.content,
              report: {
                ...prev.content.report,
                cta: {
                  ...prev.content.report.cta,
                  [fieldName]: value,
                },
              },
            }
          : prev.content,
      };
    });
  };

  if (!values) return null;

  return (
    <AppDetailsContainer header={text.header} icon="file-text">
      <MainPreview
        values={values?.content}
        data={{
          appConfigData: {
            report: {
              cta: {
                helpWithBidding:
                  values?.content?.report.cta.helpWithBidding || false,
                helpWithViewing:
                  values?.content?.report.cta.helpWithViewing || false,
              },
            },
          },
        }}
        step={StepName.Report}
      />
      <Container direction="column" width="100%" padding={['base', null]}>
        <Body>
          Deze buttons worden aan de gebruiker getoond als de woning die wordt
          bekeken binnen jouw werkgebied ligt.
        </Body>

        {!hasInclusiveWorkingAreas && (
          <CallToActionBlock
            header={text.noWorkingAreaHeader}
            description={text.noWorkingAreaDescription}
            icon={{ name: 'alert-circle', background: { group: 'warning' } }}
            linkButton={{
              label: ctaButtonLabel,
              appearance: 'primary',
              icon: 'arrowRight',
              to: ctaLink,
            }}
            align="center"
          />
        )}

        <ToggleCheckbox
          label={text.helpWithBiddingLabel}
          value={values?.content?.report.cta.helpWithBidding || false}
          onChange={() => {
            onUpdateCta(
              'helpWithBidding',
              !(values?.content?.report.cta.helpWithBidding || false),
            );
          }}
          containerProps={{
            direction: 'row',
            gap: 'base',
            justification: 'space-between',
            align: 'center',
            width: '100%',
          }}
        />

        <Divider margin={[null]} />

        <ToggleCheckbox
          label={text.helpWithViewingLabel}
          value={values?.content?.report.cta.helpWithViewing || false}
          onChange={() => {
            onUpdateCta(
              'helpWithViewing',
              !(values?.content?.report.cta.helpWithViewing || false),
            );
          }}
          containerProps={{
            direction: 'row',
            gap: 'base',
            justification: 'space-between',
            align: 'center',
            width: '100%',
          }}
        />
      </Container>
    </AppDetailsContainer>
  );
};

const Container = styled(JustificationContainer)`
  gap: ${({ theme }) => theme.space('base')};
`;

export default Report;
